import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Grid } from '@material-ui/core';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Footer from '../../Common/CommonComponents/Footer';
import ImageFallback from '../../Common/CommonComponents/ImageFallback';
import DomainCheckHelper from '../../Helpers/DomainCheckHelper';
import BrandLogoHelper, { formatBrand } from '../../Helpers/BrandLogoHelper';
import { errorPing } from '../../Requests/AppRequests';

const useStyles = makeStyles((theme) => ({
  body: {
    background: 'none',
  },
  lostTitle: {
    fontSize: '10rem',
    margin: '20px 0 0 0',
    fontWeight: 700,
    textTransform: 'uppercase',
    lineHeight: '1',
    [theme.breakpoints.down('sm')]: {
      fontSize: '8rem',
    },
  },
  subTitle: {
    fontSize: '3rem',
    margin: '0',
    fontWeight: 700,
    textTransform: 'uppercase',
    lineHeight: '1',
  },
  verticalImageLayout: {
    width: '20rem',
    height: 'auto',
    [theme.breakpoints.down('sm')]: {
      marginTop: '1rem',
      width: '15rem',
    },
  },
  header: {
    position: 'relative',
    padding: '15px 0 25px 0',
    width: '100%',
    height: 'auto',
    textAlign: 'center',
    backgroundColor: '#e8f8fe',
  },
  logo: {
    width: '15%',
    margin: '0 auto',
    [theme.breakpoints.down('sm')]: {
      width: '30%',
    },
  },
  lostBody: {
    margin: '0 auto',
    maxWidth: '70%',
    textAlign: 'center',
    lineHeight: '1.5',
    [theme.breakpoints.down('sm')]: {
      padding: '0 15px',
      maxWidth: '100%',
      minHeight: 'auto',
    },
  },
  p1: {
    marginBottom: '1em',
    marginTop: '1em',
    fontSize: 18,
    lineHeight: '1.4em',
    color: 'black',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      fontSize: 14,
    },
  },
  p2: {
    marginLeft: '1em',
    fontSize: '1.125em',
    lineHeight: '1.4em',
    color: 'black',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      fontSize: 14,
      marginLeft: 0,
    },
  },
  continueLink: {
    color: '#38445f',
    textDescription: 'none',
  },
  imgFluid: {
    width: '100%',
    height: 'auto',
    [theme.breakpoints.down('sm')]: {
      marginTop: '1rem',
      width: '80',
    },
  },
  caption: {
    fontWeight: 600,
    color: '#222C4F',
    [theme.breakpoints.down('sm')]: {
      marginBottom: 0,
    },
  },
  h1Custom: {
    fontSize: 23,
    fontWeight: 300,
  },
  listStyle: {
    textAlign: 'left',
    '& li': {
      marginBottom: '1rem',
    },
  },
}));

const Flagged = function (props) {
  const classes = useStyles();
  const {
    logo, brand, ip, affid,
  } = props;
  const [logError, setLogError] = useState(false);
  const main = useSelector((store) => store.main);
  const domain = DomainCheckHelper.checkDomain();
  const getBrand = BrandLogoHelper.GetBrand('Auto-Savings', brand);
  const formatOttoImage = (extension) => {
    if (domain.indexOf('auto') >= 0) {
      return `${process.env.PUBLIC_URL}/assets/LP/Otto2/Otto_Glasses_Paint_Car_Cropped.${extension}`;
    }
    return `${process.env.PUBLIC_URL}/assets/LP/Otto2/Otto_Glasses.${extension === 'jpg' ? 'png' : extension}`;
  };
  useEffect(() => {
    if (!logError) {
      errorPing(
        affid,
        ip,
        main.international,
        main.denyListed,
        main.unsupportedState,
        main.urlParams.user_agent,
      );
      setLogError(true);
    }
  }, [affid, ip, main, logError, setLogError]);

  return (
    <div>
      <Grid>
        <div className={classes.header}>
          <img className={classes.logo} src={`https://dq3ll41r32qbq.cloudfront.net${logo}`} alt="Auto Logo" />
        </div>
      </Grid>
      <Grid container alignItems="center" justify="center" className={classes.lostBody}>
        <Grid item xs={12} md={6} xl={6}>
          <h1 className={classes.lostTitle}>404</h1>
          <h2 className={classes.subTitle}>Uh, oh!</h2>
          <p><strong>It looks like your internet service has been flagged.</strong></p>
          <p>Here are some potential reasons:</p>
          <ul className={classes.listStyle}>
            <li>
              If you’re using a VPN, we recommend accessing from your home internet connection.
            </li>
            <li>
              If you’re accessing our services from outside of the United States, we are unable
              to provide you with quotes.
            </li>
            <li>
              If your Internet address has performed suspicious activities, you may be blocked
              from our services.
            </li>
            <li>
              Unfortunately our service is only available to the 50 states. We’re unable to
              provide quotes for American Samoa, Guam, the Northern Mariana Islands, Puerto
              Rico, and the U.S. Virgin Islands.
            </li>
          </ul>
          <p>
            If none of these are applicable to your situation, we recommend emailing us at
            {' '}
            <a
              href={`mailto:info@${formatBrand(getBrand)}`}
              className={classes.continueLink}
            >
              info@
              {formatBrand(getBrand)}
            </a>
            {' '}
            so that we can get it resolved for you. For fastest assistance, please send
            us your IP address in the email, which can be found by accessing
            {' '}
            <a
              href="https://www.whatismyip.com/"
              className={classes.continueLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              What Is My IP?
            </a>
            .
          </p>
        </Grid>
        <Grid item xs={12} md={6} xl={6}>
          <ImageFallback
            className={domain === 'autoSavings' ? classes.imgFluid : classes.verticalImageLayout}
            alt="Otto 404 Page"
            fallback={formatOttoImage('jpg')}
            src={formatOttoImage('webp')}
          />
        </Grid>
      </Grid>
      <Grid><Footer {...props} /></Grid>
    </div>
  );
};

Flagged.propTypes = {
  form: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  variant_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  logo: PropTypes.string,
  brand: PropTypes.string,
  updateAppState: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

Flagged.defaultProps = {
  logo: '',
  form: '',
  variant_number: '',
  brand: '',
  updateAppState: {},
};
export default Flagged;
